import { api } from '../api.js';
import { esc, dialog, toast, guard } from '../ui.js';
import { shrinkImage, postToS3 } from '../upload.js';

const CATEGORIES = ['tours', 'wineries', 'events', 'video'];

export async function galleriesView(root) {
  let selected = null;

  const render = guard(async () => {
    const { galleries } = await api('/galleries');
    if (!selected && galleries.length) selected = galleries[0].id;
    const g = galleries.find((x) => x.id === selected);
    root.innerHTML = `
      <div class="page-head"><h1>Galleries &amp; media</h1><button class="btn small" type="button" id="new-gallery">+ New gallery</button></div>
      <div class="split">
        <div class="box" style="flex:1 1 240px;max-width:340px;padding:0"><div class="list">
          ${galleries.map((x) => `<button type="button" data-gallery="${x.id}" aria-current="${x.id === selected}"><span>${esc(x.title)}</span>
            <span class="badge ${x.status === 'published' ? 'gold' : 'dashed'}">${x.status === 'published' ? 'Live' : 'Draft'}</span></button>`).join('')
            || '<p class="muted" style="padding:16px">No galleries yet. Create one to start uploading.</p>'}
        </div></div>
        <div style="flex:999 1 520px;display:flex;flex-direction:column;gap:18px">${g ? detail(g) : ''}</div>
      </div>`;
  });

  function detail(g) {
    return `<form class="box" id="gallery-form" novalidate>
        <div class="form-grid">
          <label class="field"><span>Title</span><input name="title" value="${esc(g.title)}" required></label>
          <label class="field"><span>Filter category on the website</span><select name="category">${CATEGORIES.map((c) => `<option ${c === g.category ? 'selected' : ''}>${c}</option>`).join('')}</select></label>
        </div>
        <label class="check"><input type="checkbox" name="published" ${g.status === 'published' ? 'checked' : ''}><span>Published (visible on the website)</span></label>
        <div><button class="btn small primary" type="submit">Save gallery</button></div>
      </form>
      <div class="box">
        <div class="page-head"><h2>Photos &amp; video (${g.media.length})</h2>
          <label class="btn small primary" style="cursor:pointer">Upload<input type="file" id="upload" accept="image/jpeg,image/png,image/webp,video/mp4" multiple class="hidden"></label></div>
        <div id="progress" role="status"></div>
        <p class="small muted">JPG, PNG or WebP photos up to 25 MB; MP4 video up to 500 MB. Add a short description to each photo for guests using screen readers.</p>
        <div class="media-grid">${g.media.map((m) => `<div class="media-tile" data-media="${m.id}">
          <div class="photo">${m.kind === 'video' ? `<video src="${esc(m.url)}" preload="metadata" muted></video>` : `<img src="${esc(m.url)}" alt="${esc(m.alt_text || m.caption || '')}" loading="lazy">`}</div>
          <div class="meta">
            <input placeholder="Caption" value="${esc(m.caption || '')}" data-field="caption" aria-label="Caption">
            <input placeholder="Description for screen readers" value="${esc(m.alt_text || '')}" data-field="altText" aria-label="Image description">
            <div class="toolbar"><button class="btn small" type="button" data-save>Save</button><button class="btn small danger" type="button" data-delete>Delete</button></div>
            ${m.status !== 'ready' ? '<span class="badge dashed">Upload unfinished</span>' : ''}
          </div></div>`).join('') || '<p class="muted">Nothing uploaded yet.</p>'}</div>
      </div>`;
  }

  async function uploadOne(original) {
    const file = await shrinkImage(original);
    const { mediaId, upload } = await api('/media/upload-url', { method: 'POST', body: { galleryId: selected, contentType: file.type } });
    await postToS3(upload, file);
    await api(`/media/${mediaId}/complete`, { method: 'POST' });
  }

  root.addEventListener('change', guard(async (e) => {
    if (e.target.id !== 'upload') return;
    const files = [...e.target.files];
    const progress = root.querySelector('#progress');
    let done = 0;
    for (const file of files) {
      progress.innerHTML = `<p class="notice">Uploading ${done + 1} of ${files.length}: ${esc(file.name)}…</p>`;
      try { await uploadOne(file); done++; } catch (err) { toast(err.message, 'error'); }
    }
    toast(`${done} file${done === 1 ? '' : 's'} uploaded`);
    render();
  }));

  root.addEventListener('click', guard(async (e) => {
    const pick = e.target.closest('[data-gallery]');
    if (pick) { selected = pick.dataset.gallery; return render(); }
    if (e.target.closest('#new-gallery')) {
      const f = await dialog(`<h2>New gallery</h2>
        <label class="field"><span>Title</span><input name="title" required placeholder="October 24 Napa tour"></label>
        <label class="field"><span>Category</span><select name="category">${CATEGORIES.map((c) => `<option>${c}</option>`).join('')}</select></label>
        <div data-error></div>
        <div class="dialog-actions"><button class="btn small" value="cancel" formnovalidate>Back</button><button class="btn small primary" value="ok">Create</button></div>`);
      if (!f) return;
      const g = await api('/galleries', { method: 'POST', body: { title: f.get('title'), category: f.get('category'), status: 'draft' } });
      selected = g.id;
      return render();
    }
    const tile = e.target.closest('[data-media]');
    if (tile && e.target.closest('[data-save]')) {
      const v = (n) => tile.querySelector(`[data-field="${n}"]`).value;
      await api(`/media/${tile.dataset.media}`, { method: 'PUT', body: { caption: v('caption'), altText: v('altText') } });
      toast('Saved');
    }
    if (tile && e.target.closest('[data-delete]')) {
      if (!confirm('Delete this file permanently?')) return;
      await api(`/media/${tile.dataset.media}`, { method: 'DELETE' });
      toast('Deleted');
      render();
    }
  }));

  root.addEventListener('submit', guard(async (e) => {
    if (e.target.id !== 'gallery-form') return;
    e.preventDefault();
    const f = new FormData(e.target);
    await api(`/galleries/${selected}`, { method: 'PUT', body: { title: f.get('title'), category: f.get('category'), status: f.get('published') === 'on' ? 'published' : 'draft' } });
    toast('Gallery saved');
    render();
  }));

  render();
}
